import type { PastProject } from '../types/retrospective';

/** The flagship retrospective - also the default deep dive when none is chosen. */
const CORE_PLATFORM_MIGRATION: PastProject = {
  id: 'core-platform-migration',
  codename: 'Project Phoenix',
  name: 'Core Platform Migration',
  team: 'Platform Engineering',
  outcome: 'late',
  slipLabel: '+14 days',
  firstGapLabel: 'Week 3',
  engDaysLost: 42,
  workHours: 336,
  dollarCost: 50400,
  bugsTraced: 18,
  escalationMeetings: 9,
  escalationMails: 38,
  summary:
    'A quiet drift between the platform and identity squads compounded for five weeks before it surfaced as a release blocker.',
  deepSummary:
    'Intera traced the 14-day slip back to an integration gap that first showed up in Week 3 - a doubling of PR review time - and was absorbed as “normal friction” until it became a release blocker.',
  rootCauseLine: 'The failure mode was systemic isolation between two squads.',
  rcaParagraphs: [
    'Friction first surfaced in Week 3, when review cycles on the Auth Service API stretched from ~1.2 days to ~4.8 days. Engineers lacked context on the legacy hashing structures and the reviews stalled - but the signal was read as ordinary load, not a leading indicator.',
    'Because nothing escalated, the frontend squad kept integrating against the old API contract. By Week 5 Intera correlated a rising count of staging errors with diverging commit histories between the two squads and flagged an integration gap.',
    'The platform team assumed backward compatibility while the identity team had quietly deprecated fields to hit a security-parity deadline. Neither side knew the other’s assumption - the contract was never the shared source of truth.',
  ],
  evidenceHeadline:
    'No single tool held the story. Intera stitched it from 5 disconnected sources.',
  evidenceSources: [
    { platform: 'github', count: '60', unit: 'PRs & review times' },
    { platform: 'jira', count: '142', unit: 'tickets & transitions' },
    { platform: 'confluence', count: '12', unit: 'API contract docs' },
    { platform: 'slack', count: '38', unit: 'squad threads' },
    { platform: 'pagerduty', count: '2', unit: 'staging incidents' },
  ],
  signals: [
    {
      id: 'pr-review-doubled',
      label: 'PR review time doubled',
      dateLabel: 'Week 3',
      detail: 'Auth Service reviews went from 1.2 to 4.8 days.',
      kind: 'early-warning',
      position: 12,
      sources: ['github'],
    },
    {
      id: 'integration-gap-flagged',
      label: 'Integration gap flagged',
      dateLabel: 'Week 5',
      detail: 'Intera tied rising staging errors to diverging squads.',
      kind: 'intera-flag',
      position: 38,
      sources: ['github', 'jira', 'pagerduty'],
    },
    {
      id: 'arch-drift-confirmed',
      label: 'Contract drift confirmed',
      dateLabel: 'Week 8',
      detail: 'Identity had deprecated fields the platform still used.',
      kind: 'drift',
      position: 60,
      sources: ['confluence', 'github'],
    },
    {
      id: 'release-blocked',
      label: 'Release blocked',
      dateLabel: 'Week 10',
      detail: 'The committed ship date was missed at the integration seam.',
      kind: 'incident',
      position: 80,
      sources: ['jira', 'slack'],
    },
    {
      id: 'actual-ship',
      label: 'Shipped',
      dateLabel: 'Week 12 · +14d',
      detail: 'Released after a forced contract reconciliation.',
      kind: 'ship',
      position: 98,
      sources: ['jira', 'github'],
    },
  ],
  playbooks: [
    {
      id: 'cross-squad-contract-monitor',
      name: 'Cross-squad contract monitor',
      reason:
        'Two squads drifted on the same API contract for 5 weeks, unseen.',
      outcome:
        'A schema change with two dependent squads now blocks staging until both leads reconcile it.',
      accent: 'emerald',
      source: {
        kind: 'rca',
        label: 'Root-cause analysis',
      },
    },
    {
      id: 'velocity-drop-ping',
      name: 'Review-velocity drop ping',
      reason:
        'A 4× spike in review time read as “normal load” and was ignored.',
      outcome:
        'Review slowdowns on complex components now flag in week one, not week eight.',
      accent: 'cyan',
      source: {
        kind: 'slack',
        label: 'Slack · #platform-eng',
        reference: '#platform-eng',
        quote:
          'we got burned by review times quietly doubling and just eating it - next time we have to flag when reviews on a hairy service start creeping, not wait for it to blow up.',
        attribution: 'Maya Chen → Dan Roth · Week 9',
      },
    },
    {
      id: 'pre-merge-contract-signoff',
      name: 'Pre-merge contract sign-off',
      reason:
        'The platform team assumed backward compatibility the identity team had quietly dropped.',
      outcome:
        'A schema change that touches another squad now needs an explicit sign-off from that squad before it can merge.',
      accent: 'emerald',
      source: {
        kind: 'debrief',
        label: 'Sprint debrief',
        reference: 'phoenix-week12-debrief.md',
        quote:
          'Action item: any contract change that affects another squad must get an explicit sign-off before merge. We assumed compatibility and it cost us two weeks at the integration seam.',
        attribution: 'Platform Eng debrief · Week 12',
      },
    },
  ],
  guardingLabel: 'Now guarding 4 teams',
};

/**
 * The retrospectives library - finished projects, each studied for the same
 * class of gaps the live surfaces catch (cross-team contract drift, re-estimates
 * without notice, migrations without rollback). Each carries the cost, the RCA
 * Intera wrote, and the playbooks it extracted - the lessons that became the
 * guardrails now running across the org.
 */
export const PAST_PROJECTS: PastProject[] = [
  {
    id: 'digital-car-claims',
    codename: 'Project Motor Claim',
    name: 'Digital Car Claims',
    team: 'Claims Digitization · 6 teams',
    outcome: 'late',
    slipLabel: '+21 days',
    firstGapLabel: 'Week 1',
    engDaysLost: 33,
    workHours: 264,
    dollarCost: 39600,
    bugsTraced: 11,
    escalationMeetings: 12,
    escalationMails: 47,
    summary:
      'One requirement - the collective-policy flow and its employer number - was lost in the first handoff and stayed lost through three more, until UAT broke a week before go-live.',
    deepSummary:
      'Intera traced the three-week go-live delay to a single clause in an 18-page requirements document - the collective-policy pathway and its employer number - that vanished between the PRD and the API spec and was silently carried forward through every handoff after it.',
    rootCauseLine:
      'The detail was in the requirements - it just didn’t survive four handoffs.',
    rcaParagraphs: [
      'The gap entered in Week 1, at the very first handoff. Ran’s 18-page Motor Claim PRD required a separate reporting flow for customers on a collective workplace policy, including an employer-number field. The review meeting walked the standard flow; the exception, one clause among many, was never discussed.',
      'Each handoff narrowed it further. Yael’s API specification to IT Core carried the main fields - policy number, accident date, damage type, photos - but no employer number and no collective pathway. Tomer implemented exactly what he received: if a field wasn’t in the spec, it wasn’t needed. Michael then mapped only the spec’s fields into the claim system. Intera’s Week 4 flag - SCRUM-6 validates a policy ID only, while the PRD requires a collective-policy pathway - was the one moment the gap was visible, and it sat unrouted.',
      'In Week 9, one week before go-live, Gadi’s UAT run hit the collective-policy scenario: partial claims, claims stuck in the completeness check, representatives completing data by hand. Screens, API, integration mapping, test scenarios and Nofar’s support preparation all had to change, and the go-live moved by three weeks. In the emergency meeting nobody was wrong - it was in the requirements, it wasn’t in the spec, everyone connected exactly what they received. The detail simply didn’t survive the handoffs.',
    ],
    evidenceHeadline:
      'One clause in an 18-page document, lost across 4 handoffs. Intera read every link in the chain.',
    evidenceSources: [
      { platform: 'confluence', count: '18', unit: 'PRD & spec pages' },
      { platform: 'jira', count: '64', unit: 'tickets & transitions' },
      { platform: 'bmc-helix', count: '41', unit: 'change & incident records' },
      { platform: 'github', count: '37', unit: 'PRs & field mappings' },
    ],
    signals: [
      {
        id: 'exception-buried',
        label: 'Exception buried in the PRD',
        dateLabel: 'Week 1',
        detail:
          'The collective-policy flow sat in an 18-page document; review covered only the standard flow.',
        kind: 'early-warning',
        position: 8,
        sources: ['confluence'],
      },
      {
        id: 'spec-dropped-field',
        label: 'API spec dropped the field',
        dateLabel: 'Week 3',
        detail:
          'The spec to IT Core had no employer number and no collective pathway.',
        kind: 'drift',
        position: 24,
        sources: ['confluence'],
      },
      {
        id: 'prd-ticket-misalignment',
        label: 'PRD-ticket misalignment flagged',
        dateLabel: 'Week 4',
        detail:
          'SCRUM-6 validated a policy ID only, while the PRD required a collective pathway.',
        kind: 'intera-flag',
        position: 36,
        sources: ['confluence', 'jira'],
      },
      {
        id: 'gap-mapped-forward',
        label: 'Gap mapped into the claim system',
        dateLabel: 'Week 7',
        detail:
          'Integration mapped only the spec’s fields; the gap carried forward silently.',
        kind: 'drift',
        position: 56,
        sources: ['jira', 'github'],
      },
      {
        id: 'uat-collective-break',
        label: 'Collective claim broke in UAT',
        dateLabel: 'Week 9',
        detail:
          'Partial claims, stuck completeness checks, data completed by hand.',
        kind: 'incident',
        position: 78,
        sources: ['jira', 'slack'],
      },
      {
        id: 'motor-claim-golive',
        label: 'Went live',
        dateLabel: 'Week 13 · +21d',
        detail:
          'Screens, API, mapping, tests and support prep all reworked first.',
        kind: 'ship',
        position: 96,
        sources: ['jira'],
      },
    ],
    playbooks: [
      {
        id: 'requirement-survival-trace',
        name: 'Requirement-survival trace',
        reason:
          'A collective-policy clause vanished between the PRD and the API spec, and no later handoff could see what was already missing.',
        outcome:
          'Every PRD clause is now traced into the specs and tickets built from it - a requirement that drops out flags the day the spec lands, not in UAT.',
        accent: 'cyan',
        source: {
          kind: 'rca',
          label: 'Root-cause analysis',
        },
      },
      {
        id: 'edge-case-uat-first',
        name: 'Edge-case scenarios run first',
        reason:
          'The collective-policy scenario was tested one week before go-live - after four handoffs had already buried it.',
        outcome:
          'Exception flows named in the requirements now get their own UAT scenario in the first test cycle, not the last.',
        accent: 'emerald',
        source: {
          kind: 'debrief',
          label: 'Program debrief',
          reference: 'motor-claim-golive-debrief.md',
          quote:
            'Action item: every exception flow in the requirements gets its own line in the API spec and its own UAT scenario from cycle one. A clause buried on page 14 is the same as a clause never written.',
          attribution: 'Claims program debrief · Week 13',
        },
      },
    ],
    guardingLabel: 'Now guarding 6 teams',
  },
  CORE_PLATFORM_MIGRATION,
  {
    id: 'q3-auth-api-update',
    codename: 'Project Nexus',
    name: 'Q3 Auth API Update',
    team: 'Identity',
    outcome: 'late',
    slipLabel: '+4 days',
    firstGapLabel: 'Week 6',
    engDaysLost: 12,
    workHours: 96,
    dollarCost: 14400,
    bugsTraced: 7,
    escalationMeetings: 5,
    escalationMails: 17,
    summary:
      'A schema migration shipped without a rollback path, so a single bad deploy cost four days to unwind by hand.',
    deepSummary:
      'A four-day slip traced to a migration that merged with no rollback section - when it failed in staging there was no clean way back, and the recovery was manual.',
    rootCauseLine:
      'A migration reached review with no rollback plan and nobody caught it.',
    rcaParagraphs: [
      'The token-rotation migration was opened and reviewed in Week 6 without a documented rollback section. The reviewer approved on the logic; the missing rollback wasn’t part of anyone’s checklist.',
      'When the migration failed against staging data, there was no scripted path back. The team unwound it by hand over four days, and seven downstream bugs were traced to the half-applied state.',
      'The rule existed in the team’s wiki but not in the pipeline - a guideline people were expected to remember rather than a check the system enforced.',
    ],
    evidenceHeadline:
      'The proof was scattered across 4 tools that never talk to each other.',
    evidenceSources: [
      { platform: 'github', count: '48', unit: 'PRs & checks' },
      { platform: 'jira', count: '96', unit: 'tickets & transitions' },
      { platform: 'confluence', count: '9', unit: 'runbook pages' },
      { platform: 'pagerduty', count: '1', unit: 'staging incident' },
    ],
    signals: [
      {
        id: 'migration-opened',
        label: 'Migration opened, no rollback',
        dateLabel: 'Week 6',
        detail: 'PR merged without a documented rollback section.',
        kind: 'early-warning',
        position: 16,
        sources: ['github'],
      },
      {
        id: 'intera-flag-missing-rollback',
        label: 'Missing-rollback flagged',
        dateLabel: 'Week 6',
        detail: 'Intera marked the PR as off-playbook at review time.',
        kind: 'intera-flag',
        position: 40,
        sources: ['github', 'confluence'],
      },
      {
        id: 'staging-failure',
        label: 'Staging deploy failed',
        dateLabel: 'Week 7',
        detail: 'No clean path back from the half-applied migration.',
        kind: 'incident',
        position: 72,
        sources: ['pagerduty', 'jira'],
      },
      {
        id: 'manual-unwind',
        label: 'Unwound by hand · +4d',
        dateLabel: 'Week 8',
        detail: 'Four days of manual recovery; 7 bugs traced.',
        kind: 'ship',
        position: 96,
        sources: ['jira'],
      },
    ],
    playbooks: [
      {
        id: 'rollback-gate',
        name: 'Rollback-plan gate',
        reason:
          'A migration merged with no rollback, then failed in staging with no way back.',
        outcome:
          'A migration PR without a rollback section can no longer reach review.',
        accent: 'emerald',
        source: {
          kind: 'slack',
          label: 'Slack · #identity',
          reference: '#identity',
          quote:
            'four days of unwinding that by hand was brutal. next time, no migration goes in without a rollback section - let’s just make it a hard rule.',
          attribution: 'Priya Nair · the morning after',
        },
      },
    ],
    guardingLabel: 'Now guarding 6 teams',
  },
  {
    id: 'billing-engine-revamp',
    codename: 'Project Atlas',
    name: 'Billing Engine Revamp',
    team: 'Revenue',
    outcome: 'on-time',
    slipLabel: '',
    firstGapLabel: 'Week 1',
    engDaysLost: 2,
    workHours: 16,
    dollarCost: 2400,
    bugsTraced: 0,
    escalationMeetings: 1,
    escalationMails: 4,
    summary:
      'The ledger-migration task was scoped at one sprint. Intera checked it against three past migrations of the same shape, all of which had run 2.5 to 3 sprints, and flagged the estimate at planning - before a date was committed.',
    deepSummary:
      'Shipped on its committed date because the estimate was corrected at planning. Intera matched the ledger-migration task to three historically similar migrations - each had overrun a one-sprint guess by 1.5 to 2 sprints - and surfaced the gap before the team committed to a date it would have missed.',
    rootCauseLine:
      'The estimate was wrong on day one - history said so, and Intera read the history.',
    rcaParagraphs: [
      'At sprint-zero planning the ledger-migration task was estimated at a single sprint. The estimate looked reasonable in isolation - it was the same number the team had used on the last two migrations.',
      'Intera matched the task against three past migrations of comparable scope - dual-write windows, backfill, reconciliation - and found every one had taken 2.5 to 3 sprints, not one. It surfaced the mismatch in the planning review with the prior tickets attached.',
      'The team re-scoped to a two-and-a-half-sprint plan before committing a go-live date. The only cost was two days of re-planning. Because the date was right from the start, nothing downstream ever had to move - this is the slip that did not happen.',
    ],
    evidenceHeadline:
      'The warning was in the org’s own history - Intera read 3 past migrations no one had compared.',
    evidenceSources: [
      { platform: 'jira', count: '88', unit: 'tickets & past estimates' },
      { platform: 'github', count: '40', unit: 'PRs across 3 migrations' },
      { platform: 'confluence', count: '6', unit: 'migration runbooks' },
    ],
    signals: [
      {
        id: 'one-sprint-estimate',
        label: 'Ledger migration scoped at one sprint',
        dateLabel: 'Week 1',
        detail: 'The same guess the team had used on prior migrations.',
        kind: 'early-warning',
        position: 14,
        sources: ['jira'],
      },
      {
        id: 'history-mismatch-flag',
        label: 'Estimate flagged against history',
        dateLabel: 'Week 1',
        detail: '3 past migrations of this shape each ran 2.5 to 3 sprints.',
        kind: 'intera-flag',
        position: 36,
        sources: ['jira', 'github'],
      },
      {
        id: 'rescoped-at-planning',
        label: 'Re-scoped before commitment',
        dateLabel: 'Week 1',
        detail: 'Plan moved to 2.5 sprints; no date was ever missed.',
        kind: 'drift',
        position: 56,
        sources: ['jira'],
      },
      {
        id: 'atlas-shipped',
        label: 'Shipped on time',
        dateLabel: 'Week 12',
        detail:
          'The estimate was right from day one, so nothing downstream moved.',
        kind: 'ship',
        position: 95,
        sources: ['jira'],
      },
    ],
    playbooks: [
      {
        id: 'estimate-vs-history-check',
        name: 'Estimate-vs-history check',
        reason:
          'A one-sprint estimate matched the team’s habit, not the 2.5-to-3-sprint reality of three past migrations of the same shape.',
        outcome:
          'Every estimate is now matched against the org’s own history of similar work - a number that contradicts past actuals is flagged in the planning review, before a date is committed.',
        accent: 'cyan',
        source: {
          kind: 'rca',
          label: 'Root-cause analysis',
        },
      },
    ],
    guardingLabel: 'Now guarding 9 teams',
  },
  {
    id: 'data-pipeline-consolidation',
    codename: 'Project Vega',
    name: 'Data Pipeline Consolidation',
    team: 'Data Platform',
    outcome: 'late',
    slipLabel: '+9 days',
    firstGapLabel: 'Week 3',
    engDaysLost: 21,
    workHours: 168,
    dollarCost: 25200,
    bugsTraced: 3,
    escalationMeetings: 6,
    escalationMails: 28,
    summary:
      'Data-governance was never in the room while the consolidated store was designed. They were pulled in two weeks before launch, found raw customer PII landing unmasked in a shared bucket, and the rework cost nine days.',
    deepSummary:
      'Intera traced the nine-day slip to a stakeholder who joined too late: data-governance was looped in only two weeks before go-live, after the design that needed their masking rules was already built. The shared store had been ingesting unmasked PII the whole time.',
    rootCauseLine:
      'The team that owned the rules was pulled in after the design that needed them was built.',
    rcaParagraphs: [
      'The consolidation merged six source pipelines into one shared analytics store. The design was locked in Week 3 by Data Platform alone - data-governance, who owned the PII-masking policy, was not on the review and was not asked to be.',
      'In Week 6 Intera flagged that a project writing customer records into a shared, broadly-read bucket had no governance sign-off on its access path - a stakeholder the policy required was missing from every thread. The flag sat in a digest; no gate forced governance in.',
      'Governance was finally pulled in two weeks before launch. They found raw, unmasked PII - emails, policy numbers, payout amounts - already landing in the shared store, readable by every analytics consumer. Masking had to be retrofitted into the ingestion path, three downstream views rebuilt, and the launch moved nine days. The rules existed the whole time; the people who held them were simply never in the room.',
    ],
    evidenceHeadline:
      'The missing stakeholder was visible only by reading who was on 4 tools - and who was not.',
    evidenceSources: [
      { platform: 'jira', count: '79', unit: 'tickets & transitions' },
      { platform: 'confluence', count: '11', unit: 'design & policy pages' },
      { platform: 'slack', count: '33', unit: 'design threads' },
      { platform: 'github', count: '41', unit: 'PRs & schema changes' },
    ],
    signals: [
      {
        id: 'design-locked-no-governance',
        label: 'Design locked without governance',
        dateLabel: 'Week 3',
        detail: 'The shared-store design was reviewed by Data Platform alone.',
        kind: 'early-warning',
        position: 12,
        sources: ['confluence'],
      },
      {
        id: 'governance-missing-flag',
        label: 'Missing stakeholder flagged',
        dateLabel: 'Week 6',
        detail: 'A PII store with no governance sign-off on its access path.',
        kind: 'intera-flag',
        position: 36,
        sources: ['confluence', 'jira', 'slack'],
      },
      {
        id: 'pii-in-shared-store',
        label: 'Unmasked PII in the shared store',
        dateLabel: 'Week 9',
        detail:
          'Raw emails, policy numbers and payouts readable by all consumers.',
        kind: 'incident',
        position: 64,
        sources: ['github', 'jira'],
      },
      {
        id: 'masking-rework',
        label: 'Masking retrofitted under deadline',
        dateLabel: 'Week 10',
        detail: 'Ingestion path reworked, 3 downstream views rebuilt.',
        kind: 'drift',
        position: 82,
        sources: ['jira', 'github'],
      },
      {
        id: 'vega-shipped',
        label: 'Shipped',
        dateLabel: 'Week 12 · +9d',
        detail: 'Launched once governance had signed off the access path.',
        kind: 'ship',
        position: 96,
        sources: ['jira'],
      },
    ],
    playbooks: [
      {
        id: 'governance-signoff-gate',
        name: 'Governance sign-off gate',
        reason:
          'A project writing customer PII into a shared store reached launch week with no sign-off from the team that owned the masking policy.',
        outcome:
          'Any design that moves regulated data into a shared store now blocks at design review until the governance owner has signed off the access path.',
        accent: 'emerald',
        source: {
          kind: 'rca',
          label: 'Root-cause analysis',
        },
      },
      {
        id: 'stakeholder-coverage-check',
        name: 'Stakeholder-coverage check',
        reason:
          'The team that owned the rules was never on a thread, and nobody noticed the absence until two weeks before launch.',
        outcome:
          'Intera now maps each project to the stakeholders its data and dependencies require, and flags an owning team that has been silent through design - while it can still be invited.',
        accent: 'cyan',
        source: {
          kind: 'debrief',
          label: 'Sprint debrief',
          reference: 'vega-week11-debrief.md',
          quote:
            'Action item: governance has to be on the design review for anything touching customer data, full stop. We didn’t leave them out on purpose - they just were never on the invite, and we only noticed when it was almost too late.',
          attribution: 'Data Platform debrief · Week 11',
        },
      },
    ],
    guardingLabel: 'Now guarding 11 teams',
  },
  {
    id: 'search-relevance-upgrade',
    codename: 'Project Helios',
    name: 'Search Relevance Upgrade',
    team: 'Discovery',
    outcome: 'late',
    slipLabel: '+6 days',
    firstGapLabel: 'Week 4',
    engDaysLost: 17,
    workHours: 136,
    dollarCost: 20400,
    bugsTraced: 5,
    escalationMeetings: 5,
    escalationMails: 21,
    summary:
      'Discovery built ranking against the embeddings API they had signed off as done. The ML-platform team shipped v2 with a changed vector format and never told them - search returned garbage in staging six days before launch.',
    deepSummary:
      'A six-day slip traced to a breaking change in a dependency everyone had marked done: ML-platform shipped embeddings v2 with a new vector dimensionality, and Discovery - who had finished integrating against v1 - found out only when staging relevance collapsed.',
    rootCauseLine:
      'A finished dependency changed underneath the team that had already built on it.',
    rcaParagraphs: [
      'By Week 4 Discovery had integrated the new ranking model against the embeddings API and closed the dependency as done - the contract was stable, the work was finished, attention moved on.',
      'In Week 7 the ML-platform team shipped embeddings v2 to improve recall. It changed the vector dimensionality and normalization - a breaking change for any consumer - but it landed as a routine internal release, with no note to the squads consuming the old format. Intera flagged that a service Discovery depended on had shipped an incompatible version while Discovery’s integration was still pinned to the old one.',
      'Nobody routed the flag, because the dependency was closed - a finished item is one nobody is watching. In Week 9 staging relevance collapsed: search returned near-random results because v1-shaped queries were hitting v2 vectors. Discovery re-fit the ranking layer to the new format under deadline, traced five relevance bugs, and the launch slipped six days.',
    ],
    evidenceHeadline:
      'A breaking change in a “done” dependency, visible only across 3 tools read together.',
    evidenceSources: [
      { platform: 'github', count: '52', unit: 'PRs & version tags' },
      { platform: 'jira', count: '84', unit: 'tickets & transitions' },
      { platform: 'slack', count: '29', unit: 'cross-squad threads' },
    ],
    signals: [
      {
        id: 'built-against-v1',
        label: 'Ranking built against embeddings v1',
        dateLabel: 'Week 4',
        detail: 'Discovery closed the embeddings dependency as done.',
        kind: 'early-warning',
        position: 12,
        sources: ['github', 'jira'],
      },
      {
        id: 'v2-shipped-silently',
        label: 'Platform shipped embeddings v2',
        dateLabel: 'Week 7',
        detail: 'New vector format released with no note to consumers.',
        kind: 'drift',
        position: 38,
        sources: ['github'],
      },
      {
        id: 'consumer-break-flag',
        label: 'Incompatible-version flagged',
        dateLabel: 'Week 7',
        detail:
          'A done dependency had shipped a breaking change underneath Discovery.',
        kind: 'intera-flag',
        position: 56,
        sources: ['github', 'jira', 'slack'],
      },
      {
        id: 'staging-garbage',
        label: 'Search collapsed in staging',
        dateLabel: 'Week 9',
        detail: 'v1-shaped queries hit v2 vectors; results went near-random.',
        kind: 'incident',
        position: 78,
        sources: ['jira', 'github'],
      },
      {
        id: 'helios-shipped',
        label: 'Shipped',
        dateLabel: 'Week 10 · +6d',
        detail: 'Re-fit to v2 under deadline; 5 relevance bugs traced.',
        kind: 'ship',
        position: 96,
        sources: ['jira'],
      },
    ],
    playbooks: [
      {
        id: 'consumer-impact-alert',
        name: 'Consumer-impact alert',
        reason:
          'A dependency Discovery had marked done shipped a breaking version, and a closed item is one nobody is watching.',
        outcome:
          'When a service ships an incompatible version, Intera now alerts every squad still pinned to the old one - even when that squad has closed the dependency as done.',
        accent: 'emerald',
        source: {
          kind: 'slack',
          label: 'Slack · #discovery',
          reference: '#discovery',
          quote:
            'we marked embeddings done in week 4 and stopped looking. they shipped v2 in week 7 and nobody told us because, from their side, it was just a release. we need a ping when something we depend on breaks compat, even after we’ve closed it out.',
          attribution: 'Sofia Reyes · the day staging broke',
        },
      },
    ],
    guardingLabel: 'Now guarding 5 teams',
  },
];

/** The retrospective opened by default when none is selected yet. */
export const DEFAULT_PAST_PROJECT: PastProject = CORE_PLATFORM_MIGRATION;

/** Look up a past project by id. */
export function getPastProjectById(id: string): PastProject | undefined {
  return PAST_PROJECTS.find((project) => project.id === id);
}
